import PageBanner from '../components/PageBanner';
import useInView from '../hooks/useInView';
import SEO from '../components/SEO';

const About = () => {
    const [aaronRef, aaronVisible] = useInView();
    const [teamRef, teamVisible] = useInView();

    return (
        <div>
            <SEO
                title="About Us"
                description="Meet Aaron Wilson and the Kia Kaha team. Licensed home builders in Squamish and Whistler with over 20 years of custom construction experience in the Sea to Sky corridor."
                keywords="about Kia Kaha, Aaron Wilson contractor, Squamish builder, Whistler construction company, Sea to Sky home builder"
            />
            <PageBanner
                image="images/Kia_Kaha_Mossy_Rock_Web-18-9465.jpg"
                subtitle="Our Story"
                title="About Us"
                position="bg-[center_65%]"
            />

            {/* ── About Aaron ──────────────────────────────────────────────── */}
            <section ref={aaronRef} className="section max-w-7xl mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

                    {/* Image */}
                    <div
                        className={`relative transition-all duration-700
              ${aaronVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}
                    >
                        <img
                            src="images/aaron.jpg"
                            alt="Aaron Wilson, founder of Kia Kaha Construction"
                            className="w-full h-[500px] object-cover object-top"
                        />
                        {/* Jade accent border */}
                        <div className="absolute -bottom-4 -right-4 w-full h-full border border-jade-400 -z-10" />
                    </div>

                    {/* Text */}
                    <div
                        className={`transition-all duration-700 delay-200
              ${aaronVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}
                    >
                        <p className="font-sans text-xs tracking-[0.3em] uppercase text-jade-500 mb-4">
                            The Founder
                        </p>
                        <h2 className="font-serif text-4xl md:text-5xl font-light text-stone-800 mb-2 leading-tight">
                            About Aaron
                        </h2>
                        <div className="divider-left" />
                        <div className="font-sans text-stone-500 text-base leading-relaxed space-y-4 mt-2">
                            <p>
                                Aaron Wilson was born and raised in Havelock North, New Zealand, where he spent
                                his formative years working on the farms and orchards of the surrounding
                                countryside. Aaron started his contracting journey at 18 years old, when he
                                began his carpentry apprenticeship.
                            </p>
                            <p>
                                His story took him from residential construction in New Zealand, to Australia,
                                and England, before finally moving to Canada in 2000. In 2003, Aaron founded
                                Kia Kaha Construction in Whistler, named after a saying of the native New Zealand
                                Māori meaning <em className="text-stone-700">"stay strong"</em>, and moved to
                                Squamish in 2004 where he resides to this day.
                            </p>
                            <p>
                                It wasn't long before Aaron and his team were quickly recognized as some of the
                                most talented tradesmen in the rapidly developing housing market of the Sea to
                                Sky corridor. Today, Kia Kaha has become one of the most renowned construction
                                companies of the region and possesses an extended portfolio of beautiful homes
                                and commercial facilities.
                            </p>
                            <p>
                                Aaron's humble roots in his far away home country of New Zealand can still be
                                found in the Jade Koru pendant of the Kia Kaha logo — a Māori symbol representing
                                new life, growth, strength and peace.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── Parallax divider ─────────────────────────────────────────── */}
            <div
                className="relative h-56 md:h-64 bg-cover bg-center md:bg-fixed"
                style={{ backgroundImage: "url(images/layton-drive-3.jpg)" }}
            >
                <div className="absolute inset-0 bg-stone-950/60" />
                <div className="absolute inset-0 flex items-center justify-center px-8">
                    <p className="font-serif text-2xl md:text-3xl font-light text-white tracking-wide text-center max-w-3xl">
                        With years of experience and a meticulous attention to detail,
                        Kia Kaha will take you from foundations to finishing.
                    </p>
                </div>
            </div>

            {/* ── About Our Team ───────────────────────────────────────────── */}
            <section ref={teamRef} className="section bg-stone-50">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

                        {/* Text */}
                        <div
                            className={`transition-all duration-700
                ${teamVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}
                        >
                            <p className="font-sans text-xs tracking-[0.3em] uppercase text-jade-500 mb-4">
                                The People Behind the Work
                            </p>
                            <h2 className="font-serif text-4xl md:text-5xl font-light text-stone-800 mb-2 leading-tight">
                                Our Team
                            </h2>
                            <div className="divider-left" />
                            <div className="font-sans text-stone-500 text-base leading-relaxed space-y-4 mt-2">
                                <p>
                                    At Kia Kaha, our success is driven by a team of skilled professionals who
                                    bring unparalleled expertise and passion to every project. Our crew of
                                    seasoned project managers, carpenters, plumbers, and electricians are the
                                    backbone of Kia Kaha and uphold an uncompromising standard for excellence.
                                </p>
                                <p>
                                    Attention to detail is central to our identity and it is our commitment to
                                    make sure that every aspect of the project is executed with precision.
                                    Moreover, the safety of our crew is of the utmost importance to us and rigid
                                    protocols and industry-best practices are enforced at every stage of our work.
                                </p>
                                <p>
                                    With our team, you can be sure that your project will be pursued with a
                                    relentless energy and a dedication to see your vision fulfilled.
                                </p>
                            </div>
                        </div>

                        {/* Image */}
                        <div
                            className={`relative transition-all duration-700 delay-200
                ${teamVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}
                        >
                            <img
                                src="images/Kia_Kaha_High_Creek_Web-1.jpg"
                                alt="Kia Kaha team at work"
                                className="w-full h-[500px] object-cover"
                            />
                            <div className="absolute -bottom-4 -left-4 w-full h-full border border-jade-400 -z-10" />
                        </div>
                    </div>
                </div>
            </section>

            {/* ── Values strip removed — replaced with nothing ── */}
        </div>
    );
};

export default About;
